import { useEffect, useMemo, useState } from "react";
import { PaintBrushIcon, WrenchIcon } from "@heroicons/react/24/solid";
import {
  doesTemplateTypeSupportAbnormals,
  getTemplateTypeNotMentionedLabel,
  TemplateStyling,
  TEMPLATE_STYLES,
  booleanTemplateStyles,
} from "./TemplateUtils";
import { useTemplate } from "./TemplateProvider";
import SearchableDropdown from "../common/DropdownSearchable";
import VetRecButton from "../common/VetRecButton";
import { Modal } from "../common/Modal";
import { StylingToggle } from "../common/StylingToggle";

interface TemplateGenerateModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  sectionsAndIds: { label: string; value: string }[];
  advancedModeVisible: boolean;
  setAdvancedModeVisible: (value: boolean) => void;
  selectedSection: { label: string; value: string };
  setSelectedSection: (value: { label: string; value: string }) => void;
  advanceModeOption: boolean;
}

const UNIQUE_ITEM_TOOLTIP_CONTENT = "This style is unique to this section"

export default function TemplateStylingModal(
  {advanceModeOption, sectionsAndIds, advancedModeVisible, setAdvancedModeVisible, selectedSection, setSelectedSection, open, setOpen}: TemplateGenerateModalProps
) {
  const { styling, saveStyling, templateType, items } = useTemplate();
  const [localStyling, setLocalStyling] = useState<TemplateStyling>(styling);


  useEffect(() => {
    setLocalStyling(styling);
    if (styling.granular_styling) {
      setAdvancedModeVisible(true);
    }
  }, [styling]);

  // data
  const supportsAbnormals = useMemo<boolean>(
    () => doesTemplateTypeSupportAbnormals(templateType),
    [templateType]
  );
  const notMentionedLabel = useMemo(
    () => getTemplateTypeNotMentionedLabel(templateType),
    [templateType]
  );
  const stylesForSection = useMemo(() => {
    const base = { ...localStyling };
    if (localStyling?.granular_styling?.[selectedSection?.value]) {
      Object.keys(
        localStyling?.granular_styling?.[selectedSection?.value]
      ).forEach((style) => {
        base[style as keyof booleanTemplateStyles] =
          localStyling?.granular_styling?.[selectedSection.value][
            style as keyof booleanTemplateStyles
          ] ?? false;
      });
    }
    return base;
  }, [localStyling, selectedSection]);

  // helpers
  const getGranularStyle = (
    key: string,
    style: keyof booleanTemplateStyles,
    styles: TemplateStyling
  ) => {
    return styles.granular_styling?.[key]?.[style] ?? undefined;
  };
  const toggleBooleanStylingChange = (style: keyof booleanTemplateStyles) => {
    let currentStyle = getGranularStyle(
      selectedSection.value,
      style,
      localStyling
    );
    if (currentStyle === undefined) {
      currentStyle = localStyling[style];
    }

    // update granular styling by deleting or updating the key based on if it matched top level style
    if (selectedSection.value !== "ALL" && advancedModeVisible) {
      setLocalStyling((prevState) => {
        let newState = { ...prevState };
        newState = {
          ...newState,
          granular_styling: {
            ...newState.granular_styling,
            [selectedSection.value]: {
              ...newState.granular_styling?.[selectedSection.value],
              [style]: !currentStyle,
            },
          },
        };
        return newState;
      });
    } else {
      // update top level styling
      setLocalStyling((prevState) => ({
        ...prevState,
        [style]: !currentStyle,
      }));
    }
  };

  async function handleSaveStyling() {
    // Clean up granular styles that match top-level styles
    const cleanedStyling = { ...localStyling };
    const granularStyling = cleanedStyling.granular_styling;
    if (granularStyling) {
      Object.entries(granularStyling).forEach(([sectionKey, sectionStyles]) => {
        Object.entries(sectionStyles).forEach(([styleKey, styleValue]) => {
          if (
            styleValue === cleanedStyling[styleKey as keyof TemplateStyling]
          ) {
            delete granularStyling[sectionKey][
              styleKey as keyof booleanTemplateStyles
            ];
          }
        });
        // Remove empty section objects
        if (Object.keys(granularStyling[sectionKey]).length === 0) {
          delete granularStyling[sectionKey];
        }
      });
      // Remove empty granular_styling object
      if (Object.keys(granularStyling).length === 0) {
        delete cleanedStyling.granular_styling;
      }
    }

    await saveStyling(cleanedStyling);
    setOpen(false);
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => setOpen(false)}
      title="Style your notes"
      subTitle={
        <div className="flex flex-col items-center gap-y-2">
          <p>
            Pick the styling configuration of your notes{" "}
            {advancedModeVisible ? "by section." : ""}
          </p>
          {advancedModeVisible && (
            <div className="flex flex-col items-center justify-center relative w-full">
              <SearchableDropdown
                items={sectionsAndIds}
                useLabelAndValue={true}
                selectedItem={selectedSection?.value}
                setSelectedItem={(value) =>
                  setSelectedSection(
                    sectionsAndIds.find((s) => s.value === value) ??
                      sectionsAndIds[0]
                  )
                }
                displayValue={(item) =>
                  sectionsAndIds.find((s) => s.value === item)?.label ?? ""
                }
                placeholder="Select a section"
                outerClassName="mt-2"
              />
            </div>
          )}
        </div>
      }
      icon={
        <PaintBrushIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
      }
      maxWidth="sm:max-w-md"
    >
      <div className="flex flex-row justify-end absolute top-3 left-3">
        {!advancedModeVisible && advanceModeOption && (
          <button
            type="button"
            className={`inline-flex items-center gap-x-1 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-100 transition-colors mr-2 py-2 px-3 rounded-md`}
            onClick={() => {
              setAdvancedModeVisible(!advancedModeVisible);
            }}
          >
            <WrenchIcon className="h-4 w-4" />
            Advanced
          </button>
        )}
      </div>
      <div className="flex flex-col gap-y-4 mt-8">
        <div className="rounded-md w-full flex flex-col gap-y-4 items-center justify-between">
          <StylingToggle
            label="Title Bolded"
            checked={stylesForSection[TEMPLATE_STYLES.TITLE_BOLD]}
            onChange={() =>
              toggleBooleanStylingChange(TEMPLATE_STYLES.TITLE_BOLD)
            }
            tooltipContent={
              selectedSection.value !== "ALL" &&
              stylesForSection[TEMPLATE_STYLES.TITLE_BOLD] !==
                localStyling[TEMPLATE_STYLES.TITLE_BOLD]
                ? UNIQUE_ITEM_TOOLTIP_CONTENT
                : undefined
            }
          />
          <StylingToggle
            label="Title Italic"
            checked={stylesForSection[TEMPLATE_STYLES.TITLE_ITALIC]}
            onChange={() =>
              toggleBooleanStylingChange(TEMPLATE_STYLES.TITLE_ITALIC)
            }
            tooltipContent={
              selectedSection.value !== "ALL" &&
              stylesForSection[TEMPLATE_STYLES.TITLE_ITALIC] !==
                localStyling[TEMPLATE_STYLES.TITLE_ITALIC]
                ? UNIQUE_ITEM_TOOLTIP_CONTENT
                : undefined
            }
          />
          <StylingToggle
            label="Abnormals Bolded"
            hidden={!supportsAbnormals}
            checked={stylesForSection[TEMPLATE_STYLES.ABNORMALS_BOLDED]}
            onChange={() =>
              toggleBooleanStylingChange(TEMPLATE_STYLES.ABNORMALS_BOLDED)
            }
            tooltipContent={
              selectedSection.value !== "ALL" &&
              stylesForSection[TEMPLATE_STYLES.ABNORMALS_BOLDED] !==
                localStyling[TEMPLATE_STYLES.ABNORMALS_BOLDED]
                ? UNIQUE_ITEM_TOOLTIP_CONTENT
                : undefined
            }
          />
          <StylingToggle
            label="Abnormals Colored"
            hidden={!supportsAbnormals}
            checked={stylesForSection[TEMPLATE_STYLES.ABNORMALS_COLOR]}
            onChange={() =>
              toggleBooleanStylingChange(TEMPLATE_STYLES.ABNORMALS_COLOR)
            }
            tooltipContent={
              selectedSection.value !== "ALL" &&
              stylesForSection[TEMPLATE_STYLES.ABNORMALS_COLOR] !==
                localStyling[TEMPLATE_STYLES.ABNORMALS_COLOR]
                ? UNIQUE_ITEM_TOOLTIP_CONTENT
                : undefined
            }
          />
          <StylingToggle
            label="Abnormals Upper Cased"
            hidden={!supportsAbnormals}
            checked={stylesForSection[TEMPLATE_STYLES.ABNORMALS_UPPERCASE]}
            onChange={() =>
              toggleBooleanStylingChange(TEMPLATE_STYLES.ABNORMALS_UPPERCASE)
            }
            tooltipContent={
              selectedSection.value !== "ALL" &&
              stylesForSection[TEMPLATE_STYLES.ABNORMALS_UPPERCASE] !==
                localStyling[TEMPLATE_STYLES.ABNORMALS_UPPERCASE]
                ? UNIQUE_ITEM_TOOLTIP_CONTENT
                : undefined
            }
          />

          <StylingToggle
            label={`${notMentionedLabel} shown`}
            hidden={!notMentionedLabel}
            checked={stylesForSection[TEMPLATE_STYLES.SHOW_NOT_MENTIONED]}
            onChange={() =>
              toggleBooleanStylingChange(TEMPLATE_STYLES.SHOW_NOT_MENTIONED)
            }
            tooltipContent={
              selectedSection.value !== "ALL" &&
              stylesForSection[TEMPLATE_STYLES.SHOW_NOT_MENTIONED] !==
                localStyling[TEMPLATE_STYLES.SHOW_NOT_MENTIONED]
                ? UNIQUE_ITEM_TOOLTIP_CONTENT
                : undefined
            }
          />
          <StylingToggle
            label="Use bullet points for each entry"
            checked={stylesForSection[TEMPLATE_STYLES.BULLET_POINTS]}
            onChange={() =>
              toggleBooleanStylingChange(TEMPLATE_STYLES.BULLET_POINTS)
            }
            tooltipContent={
              selectedSection.value !== "ALL" &&
              stylesForSection[TEMPLATE_STYLES.BULLET_POINTS] !==
                localStyling[TEMPLATE_STYLES.BULLET_POINTS]
                ? UNIQUE_ITEM_TOOLTIP_CONTENT
                : undefined
            }
          />
          <StylingToggle
            label="Use bullet points for lists"
            checked={stylesForSection[TEMPLATE_STYLES.LIST_BULLET_POINTS]}
            onChange={() =>
              toggleBooleanStylingChange(TEMPLATE_STYLES.LIST_BULLET_POINTS)
            }
            tooltipContent={
              selectedSection.value !== "ALL" &&
              stylesForSection[TEMPLATE_STYLES.LIST_BULLET_POINTS] !==
                localStyling[TEMPLATE_STYLES.LIST_BULLET_POINTS]
                ? UNIQUE_ITEM_TOOLTIP_CONTENT
                : undefined
            }
          />
        </div>
        <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row gap-x-2 gap-y-2 justify-center items-center">
          <VetRecButton
            type="button"
            className="inline-flex w-60 justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            onClick={() => handleSaveStyling()}
          >
            Save Configuration
          </VetRecButton>
        </div>
      </div>
    </Modal>
  );
}
